import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Modal, Button, Spinner, Card, Form, PageHeader, StatusBadge } from '@/components/ui';
import { Project } from '../../../types/Project';
import { Task, TaskStage, TaskType } from '../../../types/Task';
import { projectService, ProjectWithTaskPagination, TaskPagination } from '../../../services/projectService';
import { useAuth } from '../../../contexts/AuthContext';
import { ForemanArea } from '../../../types/ForemanArea';
import { areasService } from '../../../services/areasService';
import { ContractArea } from '../../../types/ContractArea';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import { taskService as regularTaskService } from '../../../services/taskService';
import { routineTaskService } from '../../../services/routineTaskService';
import { RoutineTask } from '../../../types/RoutineTask';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDollars } from '../../../utils/formatDollars';
import { scrollListToTopAfterPaint } from '../../../utils/scrollListToTop';

// Import components
import ProjectModal from '../components/ProjectModal';
import ProjectDetailsTabbed from '../components/ProjectDetailsTabbed';
import { formatProjectStatus } from '../components/ProjectFilters';

import Toast, { ToastType } from '../../../components/Toast';
import ProjectCostsBreakdown from '../components/ProjectCostsBreakdown';
import ProjectEarnedRevenue from '../components/ProjectEarnedRevenue';
import ProjectQuantifiedRevenue from '../components/ProjectQuantifiedRevenue';
import { DetailStatGrid } from '../components/DetailStatGrid';

import CloseProjectModal from '../components/CloseProjectModal';
import CloseAllTasksModal from '../components/CloseAllTasksModal';
import ProjectHistoryModal from '../components/ProjectHistoryModal';
import ServerPagination from '../../../components/ServerPagination';
import { formatDateForDisplayUTC } from '../../../utils/dateUtils';

const SERVICE_AREAS = ['16', '17', '22', '25'];

interface TaskTypeOption {
  value: TaskType;
  label: string;
}

interface TaskStatusOption {
  value: TaskStage;
  label: string;
}

const TASK_TYPES: TaskTypeOption[] = [
  { value: 'Routine', label: 'Routine' },
  { value: 'Quantified', label: 'Quantified' },
  { value: 'Overhead', label: 'Overhead' },
  { value: 'Custom', label: 'Custom' },
  { value: 'H200', label: 'H200' },
  { value: 'Generic', label: 'Generic' },
  { value: 'Internal', label: 'Internal' }
];

const TASK_STATUSES: TaskStatusOption[] = [
  { value: TaskStage.PLANNED, label: 'Planned' },
  { value: TaskStage.ACTIVE, label: 'Active' },
  { value: TaskStage.ACTIVE_MARKED_AS_COMPLETE, label: 'Marked as Complete' },
  { value: TaskStage.CLOSED, label: 'Closed' },
  { value: TaskStage.CANCELLED, label: 'Cancelled' }
];

const ProjectDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canEditProject = hasPermission('project:edit');
  const canCloseProject = hasPermission('project:close');
  const canCreateProject = hasPermission('project:create');
  
  const [project, setProject] = useState<ProjectWithTaskPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProjectModalVisible, setIsProjectModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [activeTab, setActiveTab] = useState<'details' | 'tasks'>('details');
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: ToastType }>({
    show: false,
    message: '',
    type: 'success'
  });
  const [error, setError] = useState<string | null>(null);
  const tasksListRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const pendingScrollAfterCreateRef = useRef(false);

  // Task pagination state
  const [taskPagination, setTaskPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  const [taskFilters, setTaskFilters] = useState({
    taskType: '',
    taskStatus: '',
    taskSearch: ''
  });



  // Add new state for close project confirmation
  const [isCloseProjectModalVisible, setIsCloseProjectModalVisible] = useState(false);
  
  // Add state for PDF report type selection modal
  const [isPDFReportTypeModalVisible, setIsPDFReportTypeModalVisible] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [includeVendorInvoices, setIncludeVendorInvoices] = useState(false);
  const [includeTimecards, setIncludeTimecards] = useState(false);
  const [mottPdfConflict, setMottPdfConflict] = useState<{
    totalBillableBefore?: number;
    deductibleAmount?: number;
    totalBillableAfter?: number;
  } | null>(null);
  const [projectToClose, setProjectToClose] = useState<Project | null>(null);

  // Add state for close all tasks modal
  const [isCloseAllTasksModalVisible, setIsCloseAllTasksModalVisible] = useState(false);
  const [isClosingAllTasks, setIsClosingAllTasks] = useState(false);

  // Initialize pagination from URL params
  useEffect(() => {
    const taskPageFromUrl = parseInt(searchParams.get('taskPage') || '1');
    const taskPageSizeFromUrl = parseInt(searchParams.get('taskPageSize') || '20');
    const taskFiltersFromUrl = {
      taskType: searchParams.get('taskType') || '',
      taskStatus: searchParams.get('taskStatus') || '',
      taskSearch: searchParams.get('taskSearch') || ''
    };
    
    setTaskPagination(prev => ({ ...prev, page: taskPageFromUrl, pageSize: taskPageSizeFromUrl }));
    setTaskFilters(taskFiltersFromUrl);
  }, [searchParams]);

  // Fetch project when ID or pagination changes
  useEffect(() => {
    if (id) {
      fetchProject();
    }
  }, [id, taskPagination.page, taskPagination.pageSize, taskFilters]);

  // Fetch foreman areas on mount
  useEffect(() => {
    fetchForemanAreas();
  }, []);

  const updateTaskUrlParams = (newPagination?: Partial<typeof taskPagination>, newFilters?: typeof taskFilters) => {
    const params = new URLSearchParams(searchParams);
    
    if (newPagination) {
      if (newPagination.page) params.set('taskPage', newPagination.page.toString());
      if (newPagination.pageSize) params.set('taskPageSize', newPagination.pageSize.toString());
    }
    
    if (newFilters) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }
    
    setSearchParams(params);
  };

  const fetchProject = async () => {
    if (!id) {
      console.log('ProjectDetailsPage: No project ID provided');
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    
    console.log('ProjectDetailsPage: Fetching project with ID:', id);
    
    try {
      setLoading(true);
      const projectData = await projectService.getProjectById(
        parseInt(id),
        taskPagination.page,
        taskPagination.pageSize,
        taskFilters
      );

      if (requestId !== requestIdRef.current) return;
      
      // Debug: Log the project data to see what's being returned
      console.log('ProjectDetailsPage: Received project data:', {
        projectId: projectData.id,
        projectType: projectData.type,
        h200Fields: {
          h200ActivityType: projectData.h200ActivityType,
          h200Number: projectData.h200Number,
          h200ContractAreaId: projectData.h200ContractAreaId,
          h200AssetType: projectData.h200AssetType,
          h200AssetId: projectData.h200AssetId,
          h200StartLatitude: projectData.h200StartLatitude,
          h200StartLongitude: projectData.h200StartLongitude,
          h200EndLatitude: projectData.h200EndLatitude,
          h200EndLongitude: projectData.h200EndLongitude,
          h200FormAttachmentUrl: projectData.h200FormAttachmentUrl
        }
      });
      
      setProject(projectData);
      setError(null); // Clear any previous errors
      if (projectData.taskPagination) {
        setTaskPagination(prev => ({ ...prev, ...projectData.taskPagination }));
      }

      if (pendingScrollAfterCreateRef.current) {
        pendingScrollAfterCreateRef.current = false;
        scrollListToTopAfterPaint(undefined, tasksListRef.current);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error fetching project:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        status: (error as any)?.response?.status,
        data: (error as any)?.response?.data
      });
      if (error instanceof Error && error.message.includes('not found')) {
        // Set error state to show the actual error message
        setError(error.message);
      } else {
        setError('Failed to load project. Please try again.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchForemanAreas = async () => {
    try {
      const areas = await areasService.getForemanAreas();
      const convertedAreas = areas.map(area => ({
        id: area.id,
        name: area.name,
        serviceAreaId: area.serviceArea?.id,
        serviceArea: area.serviceArea ? {
          ...area.serviceArea,
          foremanAreas: [],
          activities: []
        } : undefined,
        contractAreas: area.contractAreas?.map(ca => ({
          id: ca.id,
          name: ca.name,
          code: ca.code || '',
          foremanAreaId: area.id,
          foremanArea: {
            id: area.id,
            name: area.name,
            serviceAreaId: area.serviceArea.id,
            serviceArea: {
              ...area.serviceArea,
              foremanAreas: [],
              activities: []
            },
            managerArea: ''
          }
        })) || []
      }));
      setForemanAreas(convertedAreas as unknown as ForemanArea[]);
    } catch (error) {
      console.error('Error fetching foreman areas:', error);
    }
  };

  const handleEditProject = () => {
    setIsProjectModalVisible(true);
  };

  const handleBackToList = () => {
    const listSearch = (location.state as { projectsListSearch?: string } | null)
      ?.projectsListSearch;
    navigate(listSearch ? `/projects?${listSearch}` : '/projects');
  };

  const handleSaveProject = async (projectData: Partial<Project>, h200FormFile?: File) => {
    if (!project) return;
    
    try {
      setLoading(true);
      
      // First update the project data
      const updatedProject = await projectService.updateProject(project.id, projectData);
      
      // If there's an H200 form file to upload, handle it separately
      if (h200FormFile && projectData.type === 'H200') {
        try {
          await projectService.uploadH200Form(project.id, h200FormFile);
          // Refresh project data to get the updated form attachment URL
          const refreshedProject = await projectService.getProjectById(
            project.id,
            taskPagination.page,
            taskPagination.pageSize,
            taskFilters
          );
          setProject({ ...refreshedProject, tasks: project.tasks, taskPagination: project.taskPagination });
        } catch (uploadError) {
          console.error('Error uploading H200 form:', uploadError);
          setToast({
            show: true,
            message: 'Project updated but H200 form upload failed. Please try uploading the form again.',
            type: 'warning'
          });
        }
      } else {
        setProject({ ...updatedProject, tasks: project.tasks, taskPagination: project.taskPagination });
      }
      
      setIsProjectModalVisible(false);
      setToast({
        show: true,
        message: 'Project updated successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving project:', error);
      setToast({
        show: true,
        message: 'Error updating project. Please try again.',
        type: 'error'
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleTaskFilterChange = (name: string, value: string) => {
    const newFilters = { ...taskFilters, [name]: value };
    setTaskFilters(newFilters);
    updateTaskUrlParams({ page: 1 }, newFilters); // Reset to page 1 when filtering
  };

  const handleTaskPageChange = (page: number) => {
    const newPagination = { ...taskPagination, page };
    setTaskPagination(newPagination);
    updateTaskUrlParams({ page }, taskFilters);
  };

  const handleTaskPageSizeChange = (pageSize: number) => {
    const newPagination = { ...taskPagination, pageSize, page: 1 };
    setTaskPagination(newPagination);
    updateTaskUrlParams({ pageSize, page: 1 }, taskFilters);
  };

  const handleTaskSaved = async (
    task: Task | RoutineTask,
    options?: { successToast?: string; isNew?: boolean }
  ) => {
    try {
      const isNew = options?.isNew === true;
      if (isNew) {
        pendingScrollAfterCreateRef.current = true;
        const urlPage = parseInt(searchParams.get('taskPage') || '1', 10);
        if (urlPage !== 1 || taskPagination.page !== 1) {
          updateTaskUrlParams({ page: 1 }, taskFilters);
        } else {
          await fetchProject();
        }
      } else {
        await fetchProject();
      }
      setToast({
        show: true,
        message: options?.successToast ?? 'Task saved successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error handling task save:', error);
      setToast({
        show: true,
        message: 'Error saving task. Please try again.',
        type: 'error'
      });
    }
  };



  const handleCloseProject = async (project: Project) => {
    setProjectToClose(project);
    setIsCloseProjectModalVisible(true);
  };

  const handleCloseAllTasks = async (comments: string, defaultCompletionDate: string, quantifiedTaskAction: 'INTERNAL_REVIEW' | 'CLOSED') => {
    if (!project) return;
    
    try {
      setIsClosingAllTasks(true);
      
      await projectService.closeAllTasks(
        project.id,
        comments,
        defaultCompletionDate,
        quantifiedTaskAction
      );
      
      // Refresh the project data to show updated task statuses
      await fetchProject();
      
      setIsCloseAllTasksModalVisible(false);
      setToast({
        show: true,
        message: 'All tasks closed successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error closing all tasks:', error);
      setToast({
        show: true,
        message: 'Error closing tasks. Please try again.',
        type: 'error'
      });
    } finally {
      setIsClosingAllTasks(false);
    }
  };

  const calculateTotalCost = (project: Project): number => {
    // Use aggregated totals from backend (calculated from ALL tasks, not just paginated ones)
    if (project.aggregatedTotals?.baseCosts) {
      return project.aggregatedTotals.baseCosts.total;
    }
    return 0;
  };

  const calculateEarnedRevenueTotal = (project: Project): number => {
    // Quantified projects report Actual Revenue (task-value based) as the headline revenue figure.
    if (project.type === 'Quantified') {
      return project.aggregatedTotals?.quantifiedRevenue?.actual ?? 0;
    }
    // Use aggregated totals from backend (calculated from ALL tasks, not just paginated ones)
    if (project.aggregatedTotals?.earnedRevenue) {
      return project.aggregatedTotals.earnedRevenue.total;
    }
    return 0;
  };

  const handlePrintProjectPDF = () => {
    if (!project) return;
    setIncludeVendorInvoices(false);
    setIncludeTimecards(false);
    setIsGeneratingPDF(false);
    setIsPDFReportTypeModalVisible(true);
  };

  const handleGeneratePDF = async (
    reportType: 'internal' | 'mott',
    options?: { allowNegativeMoTTTotals?: boolean }
  ) => {
    if (!project || isGeneratingPDF) return;

    setIsGeneratingPDF(true);

    try {
      const requestBody: {
        projectId: number;
        reportType: 'internal' | 'mott';
        includeVendorInvoices?: boolean;
        includeTimecards?: boolean;
        allowNegativeMoTTTotals?: boolean;
      } = {
        projectId: project.id,
        reportType: reportType,
      };

      if (reportType === 'mott') {
        requestBody.includeVendorInvoices = includeVendorInvoices;
        requestBody.includeTimecards = includeTimecards;
        if (options?.allowNegativeMoTTTotals) {
          requestBody.allowNegativeMoTTTotals = true;
        }
      }

      // Call the PDF generation endpoint
      const response = await fetch('/api/pdf/generate-project-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const contentType = response.headers.get('Content-Type') || '';
        if (response.status === 409 && contentType.includes('application/json')) {
          const errBody = await response.json().catch(() => null);
          if (errBody?.code === 'MOTT_NEGATIVE_BILLABLE') {
            setIsPDFReportTypeModalVisible(false);
            setIncludeVendorInvoices(false);
            setIncludeTimecards(false);
            setMottPdfConflict({
              totalBillableBefore: errBody.totalBillableBefore,
              deductibleAmount: errBody.deductibleAmount,
              totalBillableAfter: errBody.totalBillableAfter
            });
            return;
          }
        }
        throw new Error('Failed to generate PDF');
      }

      // Get the PDF blob
      const pdfBlob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      const reportTypeLabel = reportType === 'mott' ? 'MoTT' : 'Internal';
      link.download = `${reportTypeLabel}_Project_${project.projectNumber || project.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsPDFReportTypeModalVisible(false);
      setIncludeVendorInvoices(false);
      setIncludeTimecards(false);
      
    } catch (error) {
      console.error('Error generating project PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };



  if (loading && !project) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '200px' }}>
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  if (!project && !loading) {
    return (
      <div className="tw-w-full tw-p-4">
        <div className="tw-text-center">
          <h3>{error || 'Project not found'}</h3>
          <p className="tw-text-content-muted tw-mb-4">
            {error === 'Project not found or you do not have permission to view this project'
              ? 'You may not have permission to view this project, or it may not exist. Please contact your administrator if you believe this is an error.'
              : error === 'Project not found'
              ? 'The project you are looking for does not exist.'
              : 'There was an error loading the project. Please check your connection and try again.'
            }
          </p>
          <Button variant="primary" onClick={handleBackToList}>
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-p-4">
      {/* Header — was six full-size buttons in a row with no wrap/shrink, which is what QA saw
          overflow on a phone. PageHeader gives every action size="sm", right-aligns them, wraps
          instead of clipping, and collapses labels to icon-only below `sm`. */}
      <PageHeader
        title={project?.name}
        // The badge goes in the subtitle slot, NOT inside the title. PageHeader's title is
        // `truncate` (overflow:hidden), so on a 414px phone the badge was laid out at x=387..438
        // and simply clipped away — the project's status was invisible on the screen where it is
        // least guessable. The subtitle line is not truncated.
        //
        // soft + the project vocabulary keep the tinted pill this header has always had: a
        // page-level status should label the page, not compete with its actions.
        subtitle={
          project ? (
            <StatusBadge
              status={project.status}
              vocabulary="project"
              label={formatProjectStatus(project.status)}
              soft
            />
          ) : undefined
        }
        actions={[
          {
            key: 'back',
            label: 'Back to Projects',
            icon: 'bi-arrow-left',
            intent: 'secondary',
            onClick: handleBackToList,
          },
          {
            key: 'refresh',
            label: 'Refresh',
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: fetchProject,
            disabled: loading,
            title: 'Refresh project data',
          },
          {
            key: 'print',
            label: 'Print PDF',
            icon: 'bi-file-earmark-pdf',
            intent: 'neutral',
            onClick: handlePrintProjectPDF,
            disabled: loading || !project,
            title: 'Print project as PDF',
          },
          {
            key: 'history',
            label: 'View History',
            icon: 'bi-clock-history',
            intent: 'neutral',
            onClick: () => setIsHistoryModalVisible(true),
            hidden: !project,
            title: 'View project history',
          },
          {
            key: 'close-all',
            label: 'Close All Tasks',
            icon: 'bi-lock',
            intent: 'danger',
            // Amber, not red: this is a bulk status change, not a delete — same colour the
            // hand-rolled button used before the PageHeader migration.
            variant: 'outline-warning',
            onClick: () => setIsCloseAllTasksModalVisible(true),
            hidden: !(canCloseProject && project?.tasks && project.tasks.length > 0),
            title: 'Close all tasks in this project',
          },
          {
            key: 'edit',
            label: 'Edit Project',
            icon: 'bi-pencil',
            intent: 'primary',
            onClick: handleEditProject,
            hidden: !canEditProject,
            title: 'Edit project details',
          },
        ]}
      />

      {project && (
        <>
          {/* Project Info Card */}
          <Card className="tw-mb-4">
            <Card.Header>
              <h6 className="tw-mb-0">Project Information</h6>
            </Card.Header>
            <Card.Body>
              <DetailStatGrid>
                <div>
                  <Card className="tw-h-full">
                    <Card.Body className="tw-p-2">
                      <Card.Subtitle>Project Number</Card.Subtitle>
                      <p className="tw-mb-0">{project.projectNumber || 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </div>
                <div>
                  <Card className="tw-h-full">
                    <Card.Body className="tw-p-2">
                      <Card.Subtitle>Project Type</Card.Subtitle>
                      <p className="tw-mb-0">{project.type.toUpperCase()}</p>
                    </Card.Body>
                  </Card>
                </div>
                <div>
                  <Card className="tw-h-full">
                    <Card.Body className="tw-p-2">
                      <Card.Subtitle>Job Number</Card.Subtitle>
                      <p className="tw-mb-0">
                        {project.jobNumber ? (
                          project.jobNumber
                        ) : (
                          // Only show "Missing" if project type is NOT Internal, Quantified, or Routine
                          project.type !== 'Internal' && project.type !== 'Quantified' && project.type !== 'Routine' ? (
                            <span className="tw-inline-block tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-rounded tw-bg-danger/10 tw-text-danger tw-border tw-border-danger">Missing</span>
                          ) : (
                            <span className="tw-text-content-muted">-</span>
                          )
                        )}
                      </p>
                    </Card.Body>
                  </Card>
                </div>
                <div>
                  <Card className="tw-h-full">
                    <Card.Body className="tw-p-2">
                      <Card.Subtitle>Service Area</Card.Subtitle>
                      <p className="tw-mb-0">{project.serviceArea?.id || 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </div>
                <div>
                  <Card className="tw-h-full">
                    <Card.Body className="tw-p-2">
                      <Card.Subtitle>Start Date</Card.Subtitle>
                      <p className="tw-mb-0">
                        {/* UTC-midnight value: local formatting would show the previous day. */}
                        {project.startDate
                          ? formatDateForDisplayUTC(project.startDate)
                          : 'Not set'}
                      </p>
                    </Card.Body>
                  </Card>
                </div>
                <div>
                  <Card className="tw-h-full">
                    <Card.Body className="tw-p-2">
                      <Card.Subtitle>Budget</Card.Subtitle>
                      <p className="tw-mb-0">
                        {formatDollars(project.budget ?? 0)}
                      </p>
                    </Card.Body>
                  </Card>
                </div>
              </DetailStatGrid>
            </Card.Body>
          </Card>

          {/* H200 Details Card - Only show for H200 projects */}
          {project.type === 'H200' && (
            <Card className="tw-mb-4">
              <Card.Header>
                <h6 className="tw-mb-0">H200 Details</h6>
              </Card.Header>
              <Card.Body>
                {/* Debug information */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="tw-mb-3 tw-p-2 tw-bg-surface-alt tw-border tw-border-line tw-rounded" style={{ fontSize: '12px' }}>
                    <strong>H200 Debug Info:</strong><br />
                    Raw H200 Fields: {JSON.stringify({
                      h200ActivityType: project.h200ActivityType,
                      h200Number: project.h200Number,
                      h200ContractAreaId: project.h200ContractAreaId,
                      h200AssetType: project.h200AssetType,
                      h200AssetId: project.h200AssetId,
                      h200StartLatitude: project.h200StartLatitude,
                      h200StartLongitude: project.h200StartLongitude,
                      h200EndLatitude: project.h200EndLatitude,
                      h200EndLongitude: project.h200EndLongitude,
                      h200FormAttachmentUrl: project.h200FormAttachmentUrl,
                      h200DeductibleApplies: project.h200DeductibleApplies,
                      h200DeductibleAmount: project.h200DeductibleAmount
                    }, null, 2)}
                    <br /><br />
                    <strong>Related Objects:</strong><br />
                    Contract Area: {JSON.stringify(project.h200Project?.contractArea, null, 2)}<br />
                    Asset: {JSON.stringify(project.h200Project?.asset, null, 2)}
                  </div>
                )}
                
                <DetailStatGrid>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>Activity Type</Card.Subtitle>
                        <p className="tw-mb-0">{project.h200ActivityType || 'Not set'}</p>
                      </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>H200 Number</Card.Subtitle>
                        <p className="tw-mb-0">{project.h200Number || 'Not set'}</p>
                      </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>Contract Area</Card.Subtitle>
                        <p className="tw-mb-0">
                          {project.h200Project?.contractArea ? (
                            <span title={`Code: ${project.h200Project.contractArea.code}`}>
                              {project.h200Project.contractArea.name}
                            </span>
                          ) : project.h200ContractAreaId ? (
                            `ID: ${project.h200ContractAreaId}`
                          ) : (
                            'Not set'
                          )}
                        </p>
                      </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>Asset</Card.Subtitle>
                        <p className="tw-mb-0">
                          {project.h200Project?.asset ? (
                            <span title={`Type: ${project.h200Project.asset.assetType} | Number: ${project.h200Project.asset.assetNumber}`}>
                              {project.h200Project.asset.name}
                            </span>
                          ) : project.h200AssetType ? (
                            `Type: ${project.h200AssetType}`
                          ) : (
                            'Not set'
                          )}
                        </p>
                      </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>Deductible applies (MoTT)</Card.Subtitle>
                        <p className="tw-mb-0">{project.h200DeductibleApplies === true ? 'Yes' : 'No'}</p>
                      </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>Deductible amount</Card.Subtitle>
                        <p className="tw-mb-0">
                          {project.h200DeductibleApplies === true &&
                          project.h200DeductibleAmount != null &&
                          !Number.isNaN(Number(project.h200DeductibleAmount))
                            ? formatDollars(Number(project.h200DeductibleAmount))
                            : '—'}
                        </p>
                      </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>GPS Coordinates</Card.Subtitle>
                        <p className="tw-mb-0">
                          {project.h200StartLatitude && project.h200StartLongitude
                            ? `Start: ${project.h200StartLatitude}, ${project.h200StartLongitude}${
                                project.h200EndLatitude && project.h200EndLongitude
                                  ? ` | End: ${project.h200EndLatitude}, ${project.h200EndLongitude}`
                                  : ''
                              }`
                            : 'Not set'
                          }
                        </p>
                      </Card.Body>
                    </Card>
                  </div>
                  <div>
                    <Card className="tw-h-full">
                      <Card.Body className="tw-p-2">
                        <Card.Subtitle>H200 Form</Card.Subtitle>
                                          <p className="tw-mb-0">
                    {project.h200FormAttachmentUrl ? (
                      <Button as="a" href={`/api/projects/${project.id}/h200-form-download`} target="_blank" rel="noopener noreferrer" variant="outline-primary" size="sm">
                        <i className="bi bi-download tw-mr-1"></i>
                        Download
                      </Button>
                    ) : (
                      'Not uploaded'
                    )}
                  </p>
                      </Card.Body>
                    </Card>
                  </div>
              </DetailStatGrid>
            </Card.Body>
          </Card>
          )}

          {/* Costs Breakdown Card */}
          <Card className="tw-mb-4">
            <Card.Header className="tw-flex tw-justify-between tw-items-center">
              <h6 className="tw-mb-0">Base Costs Breakdown (Before Markup)</h6>
              <div className="tw-text-right">
                <small className="tw-text-content-muted tw-block">Total Cost</small>
                <strong>{formatDollars(calculateTotalCost(project))}</strong>
              </div>
            </Card.Header>
            <Card.Body>
              <ProjectCostsBreakdown project={project} />
            </Card.Body>
          </Card>

          {/* Earned Revenue Card */}
          <Card className="tw-mb-4">
            <Card.Header className="tw-flex tw-justify-between tw-items-center">
              <h6 className="tw-mb-0">
                {project.type === 'Quantified'
                  ? 'Revenue (From Task Values)'
                  : 'Earned Revenue (With Markups Applied)'}
              </h6>
              <div className="tw-text-right">
                <small className="tw-text-content-muted tw-block">
                  {project.type === 'Quantified' ? 'Actual Revenue' : 'Total Revenue'}
                </small>
                <strong className="tw-text-success">{formatDollars(calculateEarnedRevenueTotal(project))}</strong>
              </div>
            </Card.Header>
            <Card.Body>
              {project.type === 'Quantified' ? (
                <ProjectQuantifiedRevenue project={project} />
              ) : (
                <ProjectEarnedRevenue project={project} />
              )}
            </Card.Body>
          </Card>

          {/* Tasks and Other Costs Tabbed Interface */}
          <ProjectDetailsTabbed
            project={project}
            foremanAreas={foremanAreas}
            onTaskSaved={handleTaskSaved}
            onProjectUpdate={(updatedProject) => {
              setProject({ ...updatedProject, tasks: project.tasks, taskPagination: project.taskPagination });
            }}
            taskPagination={taskPagination}
            onTaskPageChange={handleTaskPageChange}
            onTaskPageSizeChange={handleTaskPageSizeChange}
            loading={loading}
            tasksListRef={tasksListRef}
          />
        </>
      )}

      <ProjectModal
        visible={isProjectModalVisible}
        onClose={() => setIsProjectModalVisible(false)}
        onSave={handleSaveProject}
        project={project || undefined}
      />

      {project && (
        <ProjectHistoryModal
          visible={isHistoryModalVisible}
          onClose={() => setIsHistoryModalVisible(false)}
          projectId={project.id}
          projectName={project.name}
        />
      )}

      <CloseProjectModal
        visible={isCloseProjectModalVisible}
        project={projectToClose}
        onClose={() => {
          setIsCloseProjectModalVisible(false);
          setProjectToClose(null);
        }}
        onConfirm={async () => {
          if (projectToClose) {
            try {
              await projectService.closeProject(projectToClose.id);
              await fetchProject();
              setToast({
                show: true,
                message: 'Project closed successfully!',
                type: 'success'
              });
            } catch (error) {
              setToast({
                show: true,
                message: 'Error closing project. Please try again.',
                type: 'error'
              });
            }
          }
          setIsCloseProjectModalVisible(false);
          setProjectToClose(null);
        }}
      />

      {/* PDF Report Type Selection Modal */}
      <Modal
        show={isPDFReportTypeModalVisible || isGeneratingPDF}
        onHide={() => {
          if (isGeneratingPDF) return;
          setIsPDFReportTypeModalVisible(false);
          setIncludeVendorInvoices(false);
          setIncludeTimecards(false);
        }}
        backdrop={isGeneratingPDF ? 'static' : true}
        keyboard={!isGeneratingPDF}
        size="lg"
      >
        <Modal.Header closeButton={!isGeneratingPDF}>
          <Modal.Title>Generate Project PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isGeneratingPDF ? (
            <div className="tw-text-center tw-py-5">
              <Spinner animation="border" role="status" className="tw-mb-3" />
              <p className="tw-mb-0 tw-text-content-muted">Generating PDF… This may take a moment for large projects.</p>
            </div>
          ) : (
          <>
          <p className="tw-mb-4">Select the type of project report you would like to generate:</p>
          <div className="tw-grid tw-gap-3">
            <Button
              variant="outline-primary"
              size="lg"
              onClick={() => handleGeneratePDF('internal')}
              className="tw-text-left tw-p-3 report-type-button"
              style={{
                border: '2px solid #dee2e6',
                backgroundColor: 'white',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#0d6efd';
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#dee2e6';
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <div>
                <div className="tw-font-bold tw-mb-1" style={{ fontSize: '1.1rem', color: '#212529' }}>
                  Internal Project Report
                </div>
                <div className="tw-text-content-muted" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                  Complete report including all project details, costs, revenue, and financial information
                </div>
              </div>
            </Button>
            <Button
              variant="outline-primary"
              size="lg"
              onClick={() => handleGeneratePDF('mott')}
              className="tw-text-left tw-p-3 report-type-button"
              style={{
                border: '2px solid #dee2e6',
                backgroundColor: 'white',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#0d6efd';
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#dee2e6';
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <div>
                <div className="tw-font-bold tw-mb-1" style={{ fontSize: '1.1rem', color: '#212529' }}>
                  MoTT Project Report
                </div>
                <div className="tw-text-content-muted" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                  Simplified report without financial details, rates, and certain cost information
                </div>
              </div>
            </Button>
            <div className="tw-border tw-border-line tw-rounded tw-p-3">
              <Form.Check
                type="checkbox"
                id="includeVendorInvoicesToggle"
                label="Include Vendor Invoices in MoTT PDF"
                checked={includeVendorInvoices}
                onChange={(e) => setIncludeVendorInvoices(e.target.checked)}
              />
              <small className="tw-text-content-muted tw-block tw-mt-2">
                When enabled, vendor invoice PDF attachments are appended to the end of the MoTT report.
              </small>
              <Form.Check
                className="tw-mt-3"
                type="checkbox"
                id="includeTimecardsToggle"
                label={<>Include Timecards &amp; Backup Documentation in MoTT PDF</>}
                checked={includeTimecards}
                onChange={(e) => setIncludeTimecards(e.target.checked)}
              />
              <small className="tw-text-content-muted tw-block tw-mt-2">
                When enabled, the supporting timecards and backup files for Labour, Equipment, and Hired Equipment are added to the MoTT report.
              </small>
            </div>
          </div>
          </>
          )}
        </Modal.Body>
        {!isGeneratingPDF && (
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsPDFReportTypeModalVisible(false);
              setIncludeVendorInvoices(false);
              setIncludeTimecards(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
        )}
      </Modal>

      <Modal show={Boolean(mottPdfConflict)} onHide={() => setMottPdfConflict(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>MoTT total would be negative</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-mb-2">
            After applying the deductible, the MoTT billable total would be negative. Confirm the deductible amount or
            choose how to proceed.
          </p>
          {mottPdfConflict ? (
            <ul className="tw-mb-0">
              <li>Total billable (before deductible): {formatDollars(mottPdfConflict.totalBillableBefore ?? 0)}</li>
              <li>Deductible: {formatDollars(mottPdfConflict.deductibleAmount ?? 0)}</li>
              <li>Net billable (after deductible): {formatDollars(mottPdfConflict.totalBillableAfter ?? 0)}</li>
            </ul>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMottPdfConflict(null)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            disabled={isGeneratingPDF}
            onClick={async () => {
              setMottPdfConflict(null);
              await handleGeneratePDF('mott', { allowNegativeMoTTTotals: true });
            }}
          >
            {isGeneratingPDF ? (
              <>
                <Spinner animation="border" size="sm" className="tw-mr-2" role="status" />
                Generating PDF…
              </>
            ) : (
              'Proceed anyway'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <CloseAllTasksModal
        visible={isCloseAllTasksModalVisible}
        project={project}
        onClose={() => setIsCloseAllTasksModalVisible(false)}
        onConfirm={handleCloseAllTasks}
        loading={isClosingAllTasks}
      />

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default ProjectDetailsPage; 